import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  LinearProgress,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Divider,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Email as EmailIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  Monitor as MonitorIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import bulkEmailService from '../services/bulkEmailService';

interface BulkEmailResendProps {
  eventId: string;
  eventName: string;
  onClose?: () => void;
}

interface BulkEmailStatus {
  jobId: string;
  eventId: string;
  eventName: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress: number;
  totalOrders: number;
  emailsSent: number;
  emailsFailed: number;
  startedAt: string;
  completedAt?: string;
  duration?: number;
  estimatedTimeRemaining?: number;
  errorMessage?: string;
  failedEmails: Array<{
    orderId: string;
    email: string;
    error: string;
    timestamp: string;
  }>;
  createdBy: string;
  recentLogs: Array<{
    order_id: string;
    email: string;
    status: string;
    sent_at: string;
    error_message?: string;
  }>;
}

const BulkEmailResend: React.FC<BulkEmailResendProps> = ({
  eventId,
  eventName,
  onClose,
}) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<BulkEmailStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successOrdersCount, setSuccessOrdersCount] = useState<number>(0);
  const [polling, setPolling] = useState(false);

  // Fetch initial status and success orders count
  useEffect(() => {
    if (open) {
      fetchStatus();
      fetchSuccessOrdersCount();
    }
  }, [open, eventId]);

  // Polling for real-time updates when job is processing
  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (polling && status?.status === 'processing') {
      interval = setInterval(() => {
        fetchStatus();
      }, 3000); // Poll every 3 seconds
    }
    
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [polling, status?.status]);

  const fetchSuccessOrdersCount = async () => {
    try {
      // This would be a separate API call to get count of successful orders
      // For now, we'll use the status data if available
      if (status) {
        setSuccessOrdersCount(status.totalOrders);
      }
    } catch (err) {
      console.error('Error fetching success orders count:', err);
    }
  };

  const fetchStatus = async () => {
    try {
      const response = await bulkEmailService.getBulkEmailResendStatus(eventId);
      if (response.success) {
        setStatus(response.data);
        
        // Stop polling if job is completed or failed
        if (response.data.status === 'completed' || response.data.status === 'failed') {
          setPolling(false);
        }
      }
    } catch (err: any) {
      if (err.response?.status === 404) {
        // No bulk email jobs found - this is normal
        setStatus(null);
      } else {
        console.error('Error fetching bulk email status:', err);
        setError('Failed to fetch bulk email status');
      }
    }
  };

  const handleTriggerResend = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await bulkEmailService.triggerBulkEmailResend(eventId);
      
      if (response.success) {
        setConfirmOpen(false);
        setPolling(true);
        
        // Fetch updated status
        setTimeout(() => {
          fetchStatus();
        }, 1000);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to start bulk email resend');
    } finally {
      setLoading(false);
    }
  };

  const handleRetryFailed = async () => {
    if (!status?.jobId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await bulkEmailService.retryFailedEmails(eventId, status.jobId);
      
      if (response.success) {
        setPolling(true);
        
        // Fetch updated status
        setTimeout(() => {
          fetchStatus();
        }, 1000);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to retry failed emails');
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'processing':
        return 'primary';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon />;
      case 'processing':
        return <ScheduleIcon />;
      case 'failed':
        return <ErrorIcon />;
      default:
        return <EmailIcon />;
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<EmailIcon />}
        onClick={() => setOpen(true)}
        sx={{ 
          borderColor: '#1976d2',
          color: '#1976d2',
          '&:hover': {
            borderColor: '#1565c0',
            backgroundColor: 'rgba(25, 118, 210, 0.04)',
          }
        }}
      >
        Resend All Tickets
      </Button>

      <Dialog 
        open={open} 
        onClose={() => setOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <EmailIcon color="primary" />
            <Typography variant="h6">
              Bulk Email Resend - {eventName}
            </Typography>
            <IconButton 
              size="small" 
              onClick={fetchStatus}
              disabled={loading}
            >
              <RefreshIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {status ? (
            <Box>
              {/* Status Overview */}
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={6}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Chip
                          icon={getStatusIcon(status.status)}
                          label={status.status.toUpperCase()}
                          color={getStatusColor(status.status) as any}
                          variant="outlined"
                        />
                        <Typography variant="body2" color="text.secondary">
                          Started by {status.createdBy}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="h6" align="right">
                        {status.emailsSent} / {status.totalOrders} sent
                      </Typography>
                    </Grid>
                  </Grid>

                  {status.status === 'processing' && (
                    <Box sx={{ mt: 2 }}>
                      <Box display="flex" justifyContent="space-between" mb={1}>
                        <Typography variant="body2">
                          Progress: {status.progress}%
                        </Typography>
                        {status.estimatedTimeRemaining && (
                          <Typography variant="body2" color="text.secondary">
                            ~{formatDuration(status.estimatedTimeRemaining)} remaining
                          </Typography>
                        )}
                      </Box>
                      <LinearProgress 
                        variant="determinate" 
                        value={status.progress} 
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                    </Box>
                  )}

                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={4}>
                      <Typography variant="body2" color="text.secondary">
                        Total Orders
                      </Typography>
                      <Typography variant="h6">
                        {status.totalOrders}
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="body2" color="success.main">
                        Emails Sent
                      </Typography>
                      <Typography variant="h6" color="success.main">
                        {status.emailsSent}
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="body2" color="error.main">
                        Failed
                      </Typography>
                      <Typography variant="h6" color="error.main">
                        {status.emailsFailed}
                      </Typography>
                    </Grid>
                  </Grid>

                  {status.duration && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Duration: {formatDuration(status.duration)}
                    </Typography>
                  )}
                </CardContent>
              </Card>

              {/* Failed Emails */}
              {status.failedEmails && status.failedEmails.length > 0 && (
                <Card sx={{ mb: 2 }}>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Typography variant="h6" color="error.main">
                        Failed Emails ({status.failedEmails.length})
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        onClick={handleRetryFailed}
                        disabled={loading || status.status === 'processing'}
                        startIcon={<RefreshIcon />}
                      >
                        Retry Failed
                      </Button>
                    </Box>
                    <List dense>
                      {status.failedEmails.slice(0, 5).map((failed, index) => (
                        <ListItem key={index}>
                          <ListItemText
                            primary={failed.email}
                            secondary={`Order ${failed.orderId}: ${failed.error}`}
                          />
                        </ListItem>
                      ))}
                      {status.failedEmails.length > 5 && (
                        <ListItem>
                          <ListItemText
                            secondary={`... and ${status.failedEmails.length - 5} more`}
                          />
                        </ListItem>
                      )}
                    </List>
                  </CardContent>
                </Card>
              )}

              {/* Recent Logs */}
              {status.recentLogs && status.recentLogs.length > 0 && (
                <Card>
                  <CardContent>
                    <Typography variant="h6" mb={2}>
                      Recent Activity
                    </Typography>
                    <List dense>
                      {status.recentLogs.map((log, index) => (
                        <React.Fragment key={index}>
                          <ListItem>
                            <ListItemText
                              primary={
                                <Box display="flex" alignItems="center" gap={1}>
                                  <Chip
                                    size="small"
                                    label={log.status}
                                    color={log.status === 'success' ? 'success' : 'error'}
                                    variant="outlined"
                                  />
                                  <Typography variant="body2">
                                    {log.email}
                                  </Typography>
                                </Box>
                              }
                              secondary={
                                <Box>
                                  <Typography variant="caption" color="text.secondary">
                                    Order {log.order_id} • {new Date(log.sent_at).toLocaleString()}
                                  </Typography>
                                  {log.error_message && (
                                    <Typography variant="caption" color="error.main" display="block">
                                      {log.error_message}
                                    </Typography>
                                  )}
                                </Box>
                              }
                            />
                          </ListItem>
                          {index < status.recentLogs.length - 1 && <Divider />}
                        </React.Fragment>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              )}
            </Box>
          ) : (
            <Box textAlign="center" py={4}>
              <EmailIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                No bulk email jobs found
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={3}>
                Start a bulk email resend to send tickets to all successful orders for this event.
              </Typography>
              <Button
                variant="contained"
                startIcon={<EmailIcon />}
                onClick={() => setConfirmOpen(true)}
                disabled={loading}
                sx={{ 
                  backgroundColor: '#1976d2',
                  '&:hover': {
                    backgroundColor: '#1565c0',
                  }
                }}
              >
                Start Bulk Email Resend
              </Button>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpen(false)}>
            Close
          </Button>
          <Button
            variant="outlined"
            startIcon={<MonitorIcon />}
            onClick={() => navigate(`/admin/event/${eventId}/bulk-email-monitoring`)}
          >
            View Monitoring
          </Button>
          {status && status.status !== 'processing' && (
            <Button
              variant="contained"
              startIcon={<EmailIcon />}
              onClick={() => setConfirmOpen(true)}
              disabled={loading}
              sx={{
                backgroundColor: '#1976d2',
                '&:hover': {
                  backgroundColor: '#1565c0',
                }
              }}
            >
              Start New Resend
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirm Bulk Email Resend</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Are you sure you want to resend tickets to all successful orders for "{eventName}"?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This will send emails to approximately {successOrdersCount || status?.totalOrders || 'unknown'} recipients.
            The process will be rate-limited to 5 emails per second to prevent delivery issues.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleTriggerResend}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : <EmailIcon />}
            sx={{ 
              backgroundColor: '#1976d2',
              '&:hover': {
                backgroundColor: '#1565c0',
              }
            }}
          >
            {loading ? 'Starting...' : 'Start Resend'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BulkEmailResend;
